// ═══════════════════════════════════════════════════════════════════════════
// YEAR MONTH SELECTOR - UrbanQ v1.9.6
// Selector personalizado de año y mes con soporte completo dark mode
// Reemplaza los <select> nativos que no funcionan bien en modo oscuro
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiChevronDown } from 'react-icons/fi';
import './YearMonthSelector.css';

const MONTHS = [
    { value: 1, short: 'Ene', full: 'Enero' },
    { value: 2, short: 'Feb', full: 'Febrero' },
    { value: 3, short: 'Mar', full: 'Marzo' },
    { value: 4, short: 'Abr', full: 'Abril' },
    { value: 5, short: 'May', full: 'Mayo' },
    { value: 6, short: 'Jun', full: 'Junio' },
    { value: 7, short: 'Jul', full: 'Julio' },
    { value: 8, short: 'Ago', full: 'Agosto' },
    { value: 9, short: 'Sep', full: 'Septiembre' },
    { value: 10, short: 'Oct', full: 'Octubre' },
    { value: 11, short: 'Nov', full: 'Noviembre' },
    { value: 12, short: 'Dic', full: 'Diciembre' }
];

export default function YearMonthSelector({
    year,
    month,
    onYearChange,
    onMonthChange,
    showMonth = true,
    minYear = 2020,
    maxYear = 2099,
    variant = 'default', // 'default' | 'compact' | 'gradient'
    disabled = false
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('month'); // 'month' | 'year'
    const containerRef = useRef(null);

    // Cerrar al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Cerrar con ESC
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }
        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isOpen]);

    const handleYearNav = (delta) => {
        const newYear = year + delta;
        if (newYear >= minYear && newYear <= maxYear) {
            onYearChange(newYear);
        }
    };

    const handleMonthSelect = (monthValue) => {
        onMonthChange(monthValue);
        setIsOpen(false);
    };

    const handleYearSelect = (yearValue) => {
        onYearChange(yearValue);
        if (showMonth) {
            setActiveTab('month');
        } else {
            setIsOpen(false);
        }
    };

    // Generar rango de años (10 años a la vez)
    const currentDecade = Math.floor(year / 10) * 10;
    const years = Array.from({ length: 12 }, (_, i) => currentDecade - 1 + i)
        .filter(y => y >= minYear && y <= maxYear);

    const selectedMonth = MONTHS.find(m => m.value === month);

    return (
        <div
            className={`ym-selector ${variant} ${disabled ? 'disabled' : ''}`}
            ref={containerRef}
        >
            <button
                type="button"
                className={`ym-selector-trigger ${isOpen ? 'active' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
            >
                <FiCalendar className="ym-selector-icon" />
                <span className="ym-selector-value">
                    {showMonth && selectedMonth ? `${selectedMonth.full} ` : ''}
                    {year}
                </span>
                <FiChevronDown className={`ym-selector-chevron ${isOpen ? 'open' : ''}`} />
            </button>

            {isOpen && (
                <div className="ym-selector-dropdown">
                    {/* Tabs si showMonth */}
                    {showMonth && (
                        <div className="ym-selector-tabs">
                            <button
                                type="button"
                                className={`ym-selector-tab ${activeTab === 'month' ? 'active' : ''}`}
                                onClick={() => setActiveTab('month')}
                            >
                                Mes
                            </button>
                            <button
                                type="button"
                                className={`ym-selector-tab ${activeTab === 'year' ? 'active' : ''}`}
                                onClick={() => setActiveTab('year')}
                            >
                                Año
                            </button>
                        </div>
                    )}

                    {/* Grid de meses */}
                    {showMonth && activeTab === 'month' && (
                        <div className="ym-selector-months">
                            {MONTHS.map(m => (
                                <button
                                    key={m.value}
                                    type="button"
                                    className={`ym-selector-month ${m.value === month ? 'selected' : ''}`}
                                    onClick={() => handleMonthSelect(m.value)}
                                >
                                    {m.short}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Selector de año */}
                    {(!showMonth || activeTab === 'year') && (
                        <div className="ym-selector-years-container">
                            {/* Navegacion de decadas */}
                            <div className="ym-selector-years-nav">
                                <button
                                    type="button"
                                    className="ym-selector-nav-btn"
                                    onClick={() => onYearChange(Math.max(minYear, currentDecade - 10))}
                                    disabled={currentDecade <= minYear}
                                >
                                    <FiChevronLeft />
                                </button>
                                <span className="ym-selector-decade">
                                    {currentDecade} - {currentDecade + 9}
                                </span>
                                <button
                                    type="button"
                                    className="ym-selector-nav-btn"
                                    onClick={() => onYearChange(Math.min(maxYear, currentDecade + 10))}
                                    disabled={currentDecade + 10 > maxYear}
                                >
                                    <FiChevronRight />
                                </button>
                            </div>

                            {/* Grid de años */}
                            <div className="ym-selector-years">
                                {years.map(y => (
                                    <button
                                        key={y}
                                        type="button"
                                        className={`ym-selector-year ${y === year ? 'selected' : ''} ${y === new Date().getFullYear() ? 'current' : ''}`}
                                        onClick={() => handleYearSelect(y)}
                                    >
                                        {y}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Footer con acciones rapidas */}
                    <div className="ym-selector-footer">
                        <button
                            type="button"
                            className="ym-selector-today"
                            onClick={() => {
                                const now = new Date();
                                onYearChange(now.getFullYear());
                                if (showMonth) {
                                    onMonthChange(now.getMonth() + 1);
                                }
                                setIsOpen(false);
                            }}
                        >
                            Hoy
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Exportar componente compacto para uso en headers
export function YearMonthSelectorCompact({ year, month, onYearChange, onMonthChange }) {
    return (
        <div className="ym-selector-compact">
            <div className="ym-selector-compact-group">
                <button
                    type="button"
                    className="ym-selector-compact-nav"
                    onClick={() => {
                        if (month === 1) {
                            onYearChange(year - 1);
                            onMonthChange(12);
                        } else {
                            onMonthChange(month - 1);
                        }
                    }}
                >
                    <FiChevronLeft />
                </button>

                <YearMonthSelector
                    year={year}
                    month={month}
                    onYearChange={onYearChange}
                    onMonthChange={onMonthChange}
                    variant="compact"
                />

                <button
                    type="button"
                    className="ym-selector-compact-nav"
                    onClick={() => {
                        if (month === 12) {
                            onYearChange(year + 1);
                            onMonthChange(1);
                        } else {
                            onMonthChange(month + 1);
                        }
                    }}
                >
                    <FiChevronRight />
                </button>
            </div>
        </div>
    );
}
